import React, { useEffect, useState } from 'react';
import srcFleetclearLogoStacked from '../../../assets/images/fleetclear-stacked-logo.svg';
import { Dropdown, Menu as AntMenu, Select, Modal } from 'antd';
import Icon from '../../elements/Icon';
import { getIbmIconComponent } from '../../../core/utils/iconUtils';
import { Enterprise, Erase, Event, FaceSatisfied, Help, Logout, MapBoundary, User, UserMultiple } from '@carbon/icons-react';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import * as userActions from '../../../core/user/userActions';
import * as globalActions from '../../../core/global/globalActions';
import * as deviceActions from '../../../core/device/deviceActions';
import * as assetActions from '../../../core/asset/assetActions';
import moment from 'moment';
import { getTodayInServerFormat, getYesterdayInServerFormat } from '../../../core/utils/dateUtils';
import lockr from 'lockr';
import history from '../../../core/utils/history';
import { NavLink } from 'react-router-dom/cjs/react-router-dom.min';
import FeedbackModal from '../../FeedbackModal';
import { UserRole } from '../../../types/types';
import { RootState } from '../../../core/reducers';

import './sidebar.scss';

const { SHARD_KEY } = require('../../../core/constants').default;
const { Option } = Select;

export interface SidebarProps {
    sidebarOpened: boolean;
    toggleSidebar: () => void;

}

const SidebarComponent: React.FC<SidebarProps> = ({
    sidebarOpened = true,
    toggleSidebar
}) => {
    const dispatch = useDispatch();
    const actionOptions: any = {
        ...userActions,
        ...globalActions,
        ...deviceActions,
        ...assetActions,
    };

    const actions: any = bindActionCreators(
        actionOptions,
        dispatch,
    );

    const user = useSelector((state: RootState) => state.user);
    const companies = useSelector((state: RootState) => state.company.companies);
    const { deviceGroups, userCompany } = user;
    const [shardId, setShardId] = useState<number>(lockr.get(SHARD_KEY));
    const [companyModalVisible, setCompanyModalVisible] = useState<boolean>(false);
    const [selectedUserCompany, setSelectedUserCompany] = useState<number>(userCompany?.id);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState<boolean>(false);

    let selectedDeviceGroup: any = {};

    const logout = () => {
        actions.updateMapDate(moment().format('YYYY-MM-DD'));
        actions.updateEventSearch({ assetId: null });
        actions.setAssetJourneyFilters({
            dateRange: [
                getYesterdayInServerFormat(),
                getTodayInServerFormat(),
            ],
        });
        actions.resetHeatMapData();
        actions.logoutRequest();
    };

    const handleShardChange = (e) => {
        const shardId = lockr.get(SHARD_KEY);
        if (e.key != null && e.key != shardId) {
            const newShardId = parseInt(e.key, 10);
            lockr.set(SHARD_KEY, newShardId);
            setShardId(newShardId);
            actions.setShardId(newShardId);
            actions.clearAssetMarkers();
            actions.fetchAssetMarkersRequest({ withSnailTrails: false });
            history.push('/map');
        }
    };

    const handleChangeCompany = () => {
        if (selectedUserCompany === userCompany.id) {
            return;
        }
        actions.resetHeatMapData();

        actions.updateUserCompanyRequest({ company_id: selectedUserCompany });
        setCompanyModalVisible(false);
    };

    const selectShard = () => {
        if (deviceGroups.length > 1) {
            for (let i = 0; i < deviceGroups.length; i++) {
                const deviceGroup = deviceGroups[i];
                if (deviceGroup.id == shardId) {
                    selectedDeviceGroup = deviceGroup;
                }
            }
        }
    }

    const openFeedbackModal = () => {
        setIsFeedbackModalOpen(true);
    }

    const closeFeedbackModal = () => {
        setIsFeedbackModalOpen(false);
    }

    useEffect(() => selectShard, [shardId]);
    useEffect(() => handleChangeCompany, [selectedUserCompany]);
    selectShard();


    const regionMenu = (
        <AntMenu>
            {deviceGroups.map((deviceGroup) => {
                return <AntMenu.Item disabled={deviceGroup.id == shardId} key={deviceGroup.id} onClick={handleShardChange}>{deviceGroup.label ? deviceGroup.label : deviceGroup.name}</AntMenu.Item>;
            })}
        </AntMenu>
    );

    const allowedRoles = [
        UserRole.SuperAdmin,
        UserRole.Admin,
        UserRole.Manager,
        UserRole.SalesAdmin,
    ];
    const customAllowedRoles = [
        UserRole.SuperAdmin,
        UserRole.Admin,
        UserRole.Manager,
        UserRole.User,
        UserRole.SalesAdmin,
    ];

    let isVideoVisible = false;
    if (user && user.permissions && (user.permissions.live_video === true || user.permissions.historic_video_downloading === true)) {
        isVideoVisible = true;
    }

    const navlist = [
        {
            label: 'Map', link: '/map', pathName: '/map', iconName: 'map', visible: true,
        },
        {
            label: 'Events', link: '/events', pathName: '/events', iconName: 'events', visible: true,
        },
        {
            label: 'Video', link: '/video/video-search', pathName: '/video', iconName: 'video', visible: isVideoVisible, forceRefresh: true,
        },
        {
            label: 'Dashboard', link: '/dashboard', pathName: '/dashboard', iconName: 'data', visible: true,
        },
        {
            label: 'Assets', link: '/assets', pathName: '/assets', iconName: 'assets', visible: (customAllowedRoles.length ? customAllowedRoles.includes(user.role) : true),
        },
        {
            label: 'Drivers', link: '/drivers', pathName: '/drivers', iconName: 'user', visible: (customAllowedRoles.length ? customAllowedRoles.includes(user.role) : true),
        },
        {
            label: 'Reports', link: '/reports', pathName: '/reports', iconName: 'reports', visible: true, dataTestId: 'main-navbar_reports',
        },
    ];

    const menu = (
        <AntMenu>
            <AntMenu.Item
                key="0"
                onClick={() => history.push('/profile')}
                style={{ lineHeight: '20px' }}
            >
                <User size={16} />
                &ensp;&ensp;
                My profile
            </AntMenu.Item>
            {allowedRoles.length && allowedRoles.includes(user.role) && (
                <AntMenu.Item
                    key="1"
                    onClick={() => history.push('/users')}
                    style={{ lineHeight: '20px' }}
                >
                    <UserMultiple size={16} />
                    &ensp;&ensp;
                    User management
                </AntMenu.Item>
            )}
            <AntMenu.Item
                key="2"
                onClick={() => {
                    const location = window.location;
                    location.href = 'https://help.fleetclear.com/';
                }}
                style={{ lineHeight: '20px' }}
            >
                <Help size={16} />
                &ensp;&ensp;
                Fleetclear Help
            </AntMenu.Item>
            <AntMenu.Item
                key="3"
                onClick={openFeedbackModal}
                style={{ lineHeight: '20px' }}
            >
                <FaceSatisfied size={16} />
                &ensp;&ensp;
                Help us improve
            </AntMenu.Item>
            <AntMenu.Item
                key="4"
                onClick={() => window.open('https://outlook.office365.com/owa/calendar/FleetclearTraining@fleetclear.com/bookings/', '_blank')?.focus()}
                style={{ lineHeight: '20px' }}
            >
                <Event size={16} />
                &ensp;&ensp;
                Book training session
            </AntMenu.Item>

            <AntMenu.Item
                key="5"
                onClick={logout}
                style={{ lineHeight: '20px' }}
            >
                <Logout size={16} />
                &ensp;&ensp;
                Logout
            </AntMenu.Item>
        </AntMenu>
    );

    const companiesSelect = (
        <Select
            onChange={(val) => { setSelectedUserCompany(val); }}
            showSearch
            allowClear={{ clearIcon: <Erase size={12} /> }}
            defaultValue={userCompany.title}
            optionFilterProp="children"
            filterOption={(input, option) => option?.props?.children?.toLowerCase()?.indexOf(input.toLowerCase()) >= 0}
            style={{ width: '100%' }}
            placeholder="Select Company"
        >
            {
                companies.map((v, i) => {
                    if (v.id !== userCompany.id) {
                        /* eslint-disable-next-line react/no-array-index-key */
                        return <Option key={i} value={v.id}>{v.title}</Option>;
                    }
                    return null;
                })
            }

        </Select>
    );

    const visibleItems = navlist.filter((item) => item.visible === true);

    return (
        <div className={`layout-sidebar`}>
            <div className='container'>
                <div className="sidebar__logo" >
                    <a href="/map" >
                        <img src={srcFleetclearLogoStacked} className="sidebar__logo-mini" alt="Fleetclear" />
                    </a>
                </div>
                <div className='nav-menu'>
                    {visibleItems.map((item: any, itemIndex) => (
                        <NavLink
                            to={item.link}
                            activeClassName="is-active"
                            key={item.key || `nav-item-${itemIndex}`}
                            isActive={(match, location) => location.pathname.startsWith(item.pathName)}
                            data-testid={item.dataTestId || ''}
                        >
                            <div className='nav-icon'>
                                {item.iconName && (
                                    <div>
                                        {getIbmIconComponent(item.iconName, 24)}
                                    </div>
                                )}
                            </div>
                            <span className='nav-label'>
                                {item.label}
                            </span>
                        </NavLink>
                    ))}
                </div>
                {(deviceGroups.length > 1 || user.role === UserRole.SuperAdmin || user.role === UserRole.SalesAdmin) && (
                    <div className='additional-menu nav-menu'>
                        {deviceGroups.length > 1 && (
                            <div>
                                <Dropdown overlay={regionMenu} className='region-menu' trigger={['click']} placement='topRight'>
                                    <a>
                                        <div className='nav-icon'>
                                            <MapBoundary size={24} className='map-toolbar-btn' />
                                        </div>
                                        <div className="region-label">Region</div>

                                    </a>

                                </Dropdown>
                            </div>
                        )}
                        {(user.role === UserRole.SuperAdmin || user.role === UserRole.SalesAdmin) && (
                            <div>
                                <a onClick={() => setCompanyModalVisible(true)}>
                                    <div className='nav-icon'>
                                        <Enterprise size={24} className='map-toolbar-btn' />
                                    </div>
                                    <div className="nav-label">Company</div>
                                </a>

                            </div>
                        )}
                    </div>
                )}
                <div className='footer-profile'>
                    <Dropdown overlay={menu} trigger={['hover']}>

                        <div className="profile">
                            <div className='profile-image'>
                                {user?.userCompany?.company_image ? (
                                    <img
                                        src={user?.userCompany?.company_image}
                                        alt='Profile'
                                    />
                                ) : (
                                    <Icon name="person" />
                                )}
                            </div>
                        </div>
                    </Dropdown>
                    <Modal
                        open={companyModalVisible}
                        onCancel={() => setCompanyModalVisible(false)}
                        onOk={handleChangeCompany}
                        okText="Change"
                        onClose={() => setCompanyModalVisible(false)}
                    >
                        <h3>Change Company</h3>
                        {companiesSelect}
                        <div style={{ height: '16px' }} />
                    </Modal>
                </div>
            </div>
            <FeedbackModal isModalOpen={isFeedbackModalOpen} closeModal={closeFeedbackModal} />
        </div>
    );
}

export default SidebarComponent;
